import type { CSSProperties, PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, TableRow } from '@mui/material';

import palette from '@/theme/palette.ts';

import { styles } from './styles.ts';

interface Props {
  id: UniqueIdentifier;
}

interface Context {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableTableRowContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableTableRow({ children, id }: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableTableRowContext.Provider value={context}>
      <TableRow ref={setNodeRef} style={style}>
        {children}
      </TableRow>
    </SortableTableRowContext.Provider>
  );
}

export function TableDragHandle() {
  const { attributes, listeners, ref } = useContext(SortableTableRowContext);

  return (
    <Button variant="text" sx={styles.dragHandle} {...attributes} {...listeners} ref={ref}>
      <span className="material-symbols-rounded" style={{ color: palette.text.secondary }}>
        drag_indicator
      </span>
    </Button>
  );
}
